import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getUserActivityReport } from '../../actions/dashboard';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Alert, 
    Spinner, 
    Table, 
    Badge,
    InputGroup
} from 'react-bootstrap';
import { 
    Users, 
    Download, 
    Search, 
    RefreshCw,
    MapPin,
    ShoppingCart,
    Calendar,
    Phone,
    Building2,
    Map
} from 'lucide-react';

const UserActivityReport = ({ 
    getUserActivityReport, 
    userActivityReport, 
    reportLoading, 
    reportError 
}) => {
    const [userId, setUserId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        registeredFrom: '',
        registeredTo: '',
        lastOrderFrom: '',
        lastOrderTo: '',
        lastDeliveryFrom: '',
        lastDeliveryTo: ''
    });

    useEffect(() => {
        getUserActivityReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userActivityReport && userActivityReport.data) {
            let filtered = [...userActivityReport.data];
            
            if (searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase();
                filtered = filtered.filter(user => 
                    (user.name && user.name.toLowerCase().includes(searchLower)) ||
                    (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
                    (user.sector && user.sector.toLowerCase().includes(searchLower)) ||
                    (user.serviceArea && user.serviceArea.toLowerCase().includes(searchLower))
                );
            }
            
            setFilteredData(filtered);
        }
    }, [userActivityReport, searchTerm]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleExportExcel = () => {
        const filterParams = {
            ...(userId && { userId }),
            ...(filters.registeredFrom && { registeredFrom: filters.registeredFrom }),
            ...(filters.registeredTo && { registeredTo: filters.registeredTo }),
            ...(filters.lastOrderFrom && { lastOrderFrom: filters.lastOrderFrom }),
            ...(filters.lastOrderTo && { lastOrderTo: filters.lastOrderTo }),
            ...(filters.lastDeliveryFrom && { lastDeliveryFrom: filters.lastDeliveryFrom }),
            ...(filters.lastDeliveryTo && { lastDeliveryTo: filters.lastDeliveryTo })
        };
        getUserActivityReport(filterParams, true);
    };

    const handleRefresh = () => {
        const filterParams = {
            ...(userId && { userId }),
            ...(filters.registeredFrom && { registeredFrom: filters.registeredFrom }),
            ...(filters.registeredTo && { registeredTo: filters.registeredTo }),
            ...(filters.lastOrderFrom && { lastOrderFrom: filters.lastOrderFrom }),
            ...(filters.lastOrderTo && { lastOrderTo: filters.lastOrderTo }),
            ...(filters.lastDeliveryFrom && { lastDeliveryFrom: filters.lastDeliveryFrom }),
            ...(filters.lastDeliveryTo && { lastDeliveryTo: filters.lastDeliveryTo })
        };
        getUserActivityReport(filterParams, false);
    };

    const handleClearFilters = () => {
        setUserId('');
        setFilters({
            registeredFrom: '',
            registeredTo: '',
            lastOrderFrom: '',
            lastOrderTo: '',
            lastDeliveryFrom: '',
            lastDeliveryTo: ''
        });
        getUserActivityReport({}, false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewOnMap = (coordinates) => {
        if (!coordinates) return;
        console.log(coordinates);
        const [longitude, latitude] = coordinates.split(',').map(c => c.trim());
        const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
        const mapsUrl = `${googleMapsUrl}?q=${latitude},${longitude}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h2 className="text-primary mb-1">
                                <Users size={32} className="me-3" />
                                User Activity Report
                            </h2>
                            <p className="text-muted mb-0">
                                Detailed activity report for users with delivery transactions
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleRefresh}
                                disabled={reportLoading}
                            >
                                <RefreshCw size={16} className="me-2" />
                                Refresh
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleExportExcel}
                                disabled={reportLoading}
                            >
                                <Download size={16} className="me-2" />
                                Export Excel
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {reportError && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {reportError}
                    </Alert>
                )}

                {/* Filters */}
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-filter me-2"></i>
                            Filters
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>User ID (Optional)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter user ID to filter"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Leave empty to view all users
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Search</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by name, phone, sector, or service area"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Registered From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="registeredFrom"
                                        value={filters.registeredFrom}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users registered from this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Registered To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="registeredTo"
                                        value={filters.registeredTo}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users registered until this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Order From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="lastOrderFrom"
                                        value={filters.lastOrderFrom}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with last order from this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Order To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="lastOrderTo"
                                        value={filters.lastOrderTo}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with last order until this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Delivery From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="lastDeliveryFrom"
                                        value={filters.lastDeliveryFrom}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with last delivery from this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Delivery To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="lastDeliveryTo"
                                        value={filters.lastDeliveryTo}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with last delivery until this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                onClick={handleRefresh}
                                disabled={reportLoading}
                            >
                                {reportLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <Search size={16} className="me-2" />
                                        Apply Filters
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={handleClearFilters}
                                disabled={reportLoading}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {/* Loading State */}
                {reportLoading && (
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading user activity report...</p>
                        </div>
                    </div>
                )}

                {/* Report Data */}
                {!reportLoading && userActivityReport && (
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-table me-2"></i>
                                User Activity Data
                            </h5>
                            <Badge bg="light" text="dark" className="fs-6">
                                {userActivityReport.count || filteredData.length} users
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {filteredData.length > 0 ? (
                                <div className="table-responsive">
                                    <Table striped hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Name</th>
                                                <th>Phone</th>
                                                <th>Sector</th>
                                                <th>Service Area</th>
                                                <th>GPS Coordinates</th>
                                                <th>Registered Date</th>
                                                <th>Total Cans Purchased</th>
                                                <th>Last Purchase Date</th>
                                                <th>Last Purchase Cans</th>
                                                <th>Current Cans Owned</th>
                                                <th>Last Delivery Transaction</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((user, index) => (
                                                <tr key={index}>
                                                    <td className="fw-bold">{user.name || 'N/A'}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Phone size={14} className="me-1 text-muted" />
                                                            {user.phone || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {user.sector ? (
                                                            <div className="d-flex align-items-center">
                                                                <Building2 size={14} className="me-1 text-muted" />
                                                                {user.sector}
                                                            </div>
                                                        ) : 'N/A'}
                                                    </td>
                                                    <td>
                                                        {user.serviceArea ? (
                                                            <div className="d-flex align-items-center">
                                                                <Map size={14} className="me-1 text-muted" />
                                                                {user.serviceArea}
                                                            </div>
                                                        ) : 'N/A'}
                                                    </td>
                                                    <td>
                                                        {user.gpsCoordinates ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleViewOnMap(user.gpsCoordinates)}
                                                            >
                                                                <MapPin size={12} className="me-1" />
                                                                View Map
                                                            </Button>
                                                        ) : 'N/A'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Calendar size={14} className="me-1 text-muted" />
                                                            {formatDate(user.registeredDate)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info" className="fs-6">
                                                            {user.totalCansPurchased || 0}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Calendar size={14} className="me-1 text-muted" />
                                                            {formatDate(user.lastPurchaseDate)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg="secondary">
                                                            {user.lastPurchaseCans || 0}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg="success" className="fs-6">
                                                            {user.currentCansOwned || 0}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Calendar size={14} className="me-1 text-muted" />
                                                            {formatDate(user.lastDeliveryTransactionDate)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <Users size={48} className="text-muted mb-3" />
                                    <p className="text-muted">No user activity data found.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {/* No Data State */}
                {!reportLoading && !userActivityReport && (
                    <Card className="shadow-sm">
                        <Card.Body className="text-center py-5">
                            <Users size={48} className="text-muted mb-3" />
                            <p className="text-muted">No data available. Click refresh to load the report.</p>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

UserActivityReport.propTypes = {
    getUserActivityReport: PropTypes.func.isRequired,
    userActivityReport: PropTypes.object,
    reportLoading: PropTypes.bool.isRequired,
    reportError: PropTypes.string
};

const mapStateToProps = state => ({
    userActivityReport: state.dashboard.userActivityReport,
    reportLoading: state.dashboard.reportLoading,
    reportError: state.dashboard.reportError
});

export default connect(mapStateToProps, { getUserActivityReport })(UserActivityReport);

